import { ReactElement } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useSearch, Link, useNavigate } from '@tanstack/react-router';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Paper from '@mui/material/Paper';

import { Button } from 'app-zephyr-components/Button';
import { LightHorizontalWithLogoImg } from 'app-zephyr-components/LightHorizontalWithLogoImg';
import { RouteNames } from 'app-zephyr-routes';
import { envValue } from 'app-zephyr-environment';
import { useCurrentUser } from 'app-zephyr-domains/user';
import { getAbbreviation } from 'app-zephyr-utils';

const useStyles = makeStyles()((theme) => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
  },
  navbar: {
    padding: '32px',
    display: 'flex',
    alignItems: 'center',
  },
  line: {
    width: '100%',
    flex: '1',
    height: '1px',
    marginLeft: '32px',
    border: 'none',
    borderBottom: theme.palette.border.secondary,
  },
  container: {
    display: 'flex',
    width: '100%',
    height: '80vh',
    justifyContent: 'center',
    alignItems: 'center',
  },
  paper: {
    width: '100%',
    maxWidth: '625px',
    padding: '32px',
    background: theme.palette.bg.primary.alt,
    borderRadius: '16px',
  },
  logoLink: {
    cursor: 'pointer',
  },
  iconContainer: {
    width: '48px',
    height: '48px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    backgroundColor: theme.palette.brand.purple[100],
    marginBottom: '16px',
  },
  title: {
    fontWeight: 600,
    color: theme.palette.tx.primary,
    marginBottom: '8px',
  },
  description: {
    color: theme.palette.tx.tertiary.default,
    marginBottom: '32px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: theme.palette.bg.secondary.default,
    borderRadius: '8px',
    marginBottom: '32px',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    marginRight: '16px',
  },
  userName: {
    fontWeight: 500,
    color: theme.palette.tx.primary,
  },
  userEmail: {
    color: theme.palette.tx.tertiary.default,
    fontSize: '14px',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
  },
}));

export default function AuthConfirm() {
  const { classes } = useStyles();
  const params = useSearch({ strict: false });
  const { user } = useCurrentUser();
  const navigate = useNavigate();

  // Get values from URL parameters
  const origin = (params.origin as string) || 'cli';
  const state = (params.state as string) || '';
  const redirectUri = (params.redirectUri as string) || '';

  // Format display strings based on origin
  const originDisplay = origin === 'cli' ? 'CLI' : 'Mission Control';

  // Construct the authorization URL (the login endpoint we redirect to on confirm)
  const loginUrl = new URL(envValue.value.coreApiBaseUrl);
  loginUrl.pathname = '/v2/auth/login';
  loginUrl.searchParams.append('state', state);
  loginUrl.searchParams.append('origin', origin);
  loginUrl.searchParams.append('redirectUri', redirectUri);

  return (
    <div className={classes.page}>
      <div className={classes.navbar}>
        <Link to={RouteNames.PERSONAL_DASHBOARD} search={(p: never) => p} params={(p: never) => p}>
          <LightHorizontalWithLogoImg class={classes.logoLink} />
        </Link>
        <hr className={classes.line} />
      </div>
      <div className={classes.container}>
        <Paper className={classes.paper} elevation={0}>
          <Stack alignItems="center" textAlign="center">
            <Box className={classes.iconContainer}>
              {/*TODO: We need an icon here or something*/}
              {/*<LockIcon color={theme.palette.brand.purple[500]} width={28} height={28} />*/}
            </Box>

            <Typography variant="h4" className={classes.title}>
              Authorize Zephyr {originDisplay}
            </Typography>

            <Typography variant="body1" className={classes.description}>
              You are about to authorize the Zephyr {originDisplay} with your account. Please confirm this is what you
              want to do.
            </Typography>
          </Stack>

          {user && (
            <Box className={classes.userInfo}>
              <Avatar className={classes.userAvatar}>{user.name ? getAbbreviation(user.name) : 'U'}</Avatar>
              <Box>
                <Typography variant="body1" className={classes.userName}>
                  {user.name}
                </Typography>
                <Typography variant="body2" className={classes.userEmail}>
                  {user.email}
                </Typography>
              </Box>
            </Box>
          )}

          <Box className={classes.buttonsContainer}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => {
                void navigate({ to: '/' });
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" color="primary" fullWidth href={loginUrl.toString()}>
              Authorize
            </Button>
          </Box>
        </Paper>
      </div>
    </div>
  );
}

AuthConfirm.getLayout = (page: ReactElement) => {
  return page;
};
